import { Tooltip, Typography } from "antd";
import { AutoComplete } from "antd-v5";
import { DefaultOptionType } from "antd-v5/lib/select";
import {
  ACTION_TYPE,
  filterInvalidRecords,
  lowerCaseStringExceptVariables,
  preventEnter,
} from "components/TagTable";
import { PermissionSetMap } from "constants/RBACConstants";
import _, {
  filter,
  findIndex,
  flatten,
  isEmpty,
  isFunction,
  isString,
  map,
} from "lodash";
import React, {
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import GlobalSettingAPI from "restAPI/GlobalSettingAPI";
import { hasPermission, isSuperOrTenantAdmin } from "utils/authService";
import Sorters from "utils/sorters";
import { generateUUID } from "utils/uuid";

import ACDropdown from "ac-components/ACDropdown";
import { ACTableColumnType } from "ac-components/ACTable/ACTable";
import ACButton from "ac-components/ac-button/ACButton";
import EditIcon from "ac-components/ac-icons/EditIcon";
import EllipsisVerticalIcon from "ac-components/ac-icons/EllipsisVerticalIcon";
import SaveIcon from "ac-components/ac-icons/SaveIcon";
import TrashIcon from "ac-components/ac-icons/TrashIcon";
import ACTag from "ac-components/ac-tag/ACTag";

export interface Tag {
  key: string;
  value: string;
  id?: string;
  disable?: boolean;
  isEdit?: boolean;
  newRecordAdded?: boolean;
  duplicateError?: boolean;
  requiredError?: boolean;
  reservedKeyError?: boolean;
  accountAdminOnlyError?: boolean;
  colonError?: boolean;
  lengthError?: boolean;
}

interface Props {
  source: (string | Tag)[];
  staticTags?: string[];
  mentionOptions: string[];
  preventKeys: string[];
  accountAdminOnlyKeys?: string[];
  tagsRef: RefObject<{ [key: string]: any }>;
  getAllEvents: (source: (string | Tag)[], action: string) => void;
  onChange: (
    source: (string | Tag)[],
    deleted: { deleted?: string | Tag },
  ) => void;
  [key: string]: any;
  width?: string;
  editable?: boolean;
  disableAccessText?: string;
  permission?: string;
  setEditingTags?: Dispatch<SetStateAction<boolean>>;
  disableEditActions?: boolean;
}

export default function useTagTableDetails({
  source,
  staticTags,
  getAllEvents,
  onChange,
  mentionOptions,
  preventKeys,
  accountAdminOnlyKeys = [],
  tagsRef,
  width = "200px",
  editable = false,
  disableAccessText,
  permission,
  setEditingTags,
  disableEditActions,
  ...props
}: Props) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const currentRowRef = useRef({});
  const [dataSource, setDataSource] = useState<Tag[]>([]);
  const [selectedTag, setSelectedTag] = useState<string>();
  const [tagValue, setTagValue] = useState<Tag[]>([]);
  const permissionMap =
    PermissionSetMap[permission as keyof typeof PermissionSetMap];

  useEffect(() => {
    const { promise, cancel } = GlobalSettingAPI.getTagsLibrary();
    promise.then((data) => {
      const tagArray = _.map(data.library, (item) => {
        const [key, value] = item.split(":");
        const valueArray = value?.split(",").map((v: string) => v.trim()) || [];
        return { key, value: valueArray };
      });
      setTagValue(tagArray);
    });
    return cancel;
  }, []);

  useImperativeHandle(tagsRef, () => {
    return {
      checkUnSaveChanges: () => {
        return _.some(dataSource, (tag: Tag, index: number) => {
          let keyInput = tag.key;
          let valueInput = tag.value;
          if (tag.isEdit) {
            keyInput = _.trim(
              (
                (wrapperRef.current as HTMLDivElement).querySelector(
                  `[name=key_${index}] input`,
                ) as HTMLInputElement
              ).value,
            );
            valueInput = _.trim(
              (
                (wrapperRef.current as HTMLDivElement).querySelector(
                  `[name=value_${index}] input`,
                ) as HTMLInputElement
              ).value,
            );

            return keyInput || valueInput;
          }
        });
      },
      getTags: () => dataSource,
    };
  }, [dataSource]);

  useEffect(() => {
    const newDataSource = _.map(source, (tag: string | Tag, i) => {
      if (!isString(tag)) {
        return { ...(tag as Tag), id: generateUUID() };
      }
      const tagSource = tag.split(":");
      const [key] = tagSource;
      const value = tagSource.slice(1).join(":");
      const record = { key, value, id: generateUUID() } as Tag;
      if (_.isArray(staticTags) && !_.isEmpty(staticTags)) {
        record.disable = _.includes(staticTags, tag);
      }
      return record;
    });
    setDataSource((prevSource) => {
      //merging previous data source with calculated data source
      //to get elements with isEdit true
      //to solve ENG-47492
      const editingRows = filter(prevSource, { isEdit: true });

      // For newly added rows (newRecordAdded: true), keep them as-is
      // For existing rows being edited, we need to preserve their edit state
      // by replacing the corresponding row in newDataSource
      const newlyAddedRows = editingRows.filter((row) => row.newRecordAdded);
      const existingEditingRows = editingRows.filter(
        (row) => !row.newRecordAdded,
      );

      // Replace rows in newDataSource with their editing versions if they exist
      const mergedDataSource = newDataSource.map((newRow) => {
        const editingVersion = existingEditingRows.find(
          (editRow) =>
            editRow.key === newRow.key && editRow.value === newRow.value,
        );
        return editingVersion || newRow;
      });

      return [...newlyAddedRows, ...mergedDataSource];
    });
  }, [source, staticTags]);

  const onAdd = useCallback(() => {
    setDataSource(([...source]) => {
      const newEntry = {
        key: "",
        value: "",
        newRecordAdded: true,
        isEdit: true,
      };
      (newEntry as Tag).id = generateUUID();
      source.push(newEntry);
      getAllEvents(source, ACTION_TYPE.ROW_ADD);
      return source;
    });
    setSelectedTag("");
  }, [getAllEvents]);

  const toggleEdit = useCallback(
    (rowIndex, edit = false) => {
      setDataSource(([...source]) => {
        const record = source[rowIndex] as Tag;
        if (record.newRecordAdded) {
          source.splice(rowIndex, 1);
          currentRowRef.current = {};
        } else {
          record.isEdit = edit;
          if (!edit) {
            record.duplicateError = false;
            record.requiredError = false;
            record.reservedKeyError = false;
            record.accountAdminOnlyError = false;
            record.colonError = false;
            record.lengthError = false;
          }
          currentRowRef.current = record;
        }
        getAllEvents(
          source,
          edit ? ACTION_TYPE.ROW_EDIT : ACTION_TYPE.ROW_EDIT_CANCEL,
        );
        return source;
      });
    },
    [getAllEvents, currentRowRef],
  );

  const onSave = useCallback(
    (rowIndex) => {
      setDataSource(([...source]) => {
        const record = source[rowIndex] as Tag;
        let keyInput = _.trim(
          (
            (wrapperRef.current as HTMLDivElement).querySelector(
              `[name=key_${rowIndex}] input`,
            ) as HTMLInputElement
          ).value,
        );
        // If the key field is filed
        if (keyInput.length > 0) {
          if (keyInput.includes(":")) {
            return source;
          }
          record.requiredError = false;
          let valueInput = _.trim(
            (
              (wrapperRef.current as HTMLDivElement).querySelector(
                `[name=value_${rowIndex}] input`,
              ) as HTMLInputElement
            ).value,
          );
          // The ask was to injest the tags in findings in lowercase.
          // since this component is used in runbook we need to retain
          // Armorcode variables name
          keyInput = lowerCaseStringExceptVariables(
            keyInput,
            !_.isEmpty(mentionOptions),
          );
          valueInput = lowerCaseStringExceptVariables(
            valueInput,
            !_.isEmpty(mentionOptions),
          );
          // check for duplicate key value pair
          const isDuplicate = _.find(source, (entry: Tag) => {
            return (
              entry.id !== record.id &&
              entry.key.toLowerCase() === keyInput &&
              entry.value === valueInput
            );
          });
          if (keyInput.length + valueInput.length > 996) {
            record.lengthError = true;
          } else if (isDuplicate) {
            record.duplicateError = true;
          } else if (keyInput.includes(":") || valueInput.includes(":")) {
            record.colonError = true;
          } else {
            record.duplicateError = false;
            const isReservedKeyUsed = _.includes(preventKeys, keyInput);
            const isAccountAdminOnlyKey = _.some(accountAdminOnlyKeys, (key) =>
              keyInput.startsWith(key),
            );
            if (isReservedKeyUsed) {
              record.reservedKeyError = true;
            } else if (isAccountAdminOnlyKey && !isSuperOrTenantAdmin()) {
              record.accountAdminOnlyError = true;
            } else {
              record.isEdit = false;
              record.newRecordAdded = false;
              record.reservedKeyError = false;
              record.accountAdminOnlyError = false;
              record.colonError = false;
              record.lengthError = false;
              if (!(record.key === keyInput && record.value === valueInput)) {
                record.key = keyInput;
                record.value = valueInput;
              }
              onChange(filterInvalidRecords(source), {});
            }
          }
          getAllEvents(source, ACTION_TYPE.ROW_SAVE);
        } else {
          record.requiredError = true;
        }
        currentRowRef.current = record;
        return source;
      });
    },
    [
      onChange,
      getAllEvents,
      mentionOptions,
      preventKeys,
      accountAdminOnlyKeys,
      currentRowRef,
      wrapperRef,
    ],
  );

  const onDelete = useCallback(
    (rowIndex) => {
      setDataSource(([...source]) => {
        const deletedKeyValue = source[rowIndex];
        source.splice(rowIndex, 1);
        onChange(filterInvalidRecords(source), {
          deleted: deletedKeyValue,
        });
        currentRowRef.current = {};
        getAllEvents(source, ACTION_TYPE.ROW_DELETE);
        return source;
      });
    },
    [onChange, getAllEvents, currentRowRef],
  );

  const onKeyInputChange = useCallback(
    (rowIndex, value) => {
      setDataSource((source) => {
        const record = source[rowIndex] as Tag;
        const keyInput = _.trim(
          (
            (wrapperRef.current as HTMLDivElement).querySelector(
              `[name=key_${rowIndex}] input`,
            ) as HTMLInputElement
          ).value,
        );
        const valueInput = _.trim(
          (
            (wrapperRef.current as HTMLDivElement).querySelector(
              `[name=value_${rowIndex}] input`,
            ) as HTMLInputElement
          ).value,
        );
        if (record.requiredError && _.trim(keyInput).length > 0) {
          record.requiredError = false;
          source = [...source];
        }
        if (keyInput.length === 0) {
          record.requiredError = true;
          source = [...source];
        }

        if (keyInput.length > 0 || valueInput.length > 0) {
          record.colonError =
            keyInput.includes(":") || valueInput.includes(":");
        }
        source = [...source];
        currentRowRef.current = record;
        return source;
      });
    },
    [wrapperRef, currentRowRef],
  );

  const onKeyChange = useCallback(
    (value: string, rowIndex: number) => {
      setDataSource(([...source]) => {
        const record = source[rowIndex] as Tag;
        record.key = value;
        return source;
      });
      return onKeyInputChange(rowIndex, value);
    },
    [onKeyInputChange],
  );
  const tagsActionItems = useCallback(
    (record) => {
      const isAccountAdminOnlyKey = _.some(accountAdminOnlyKeys, (key) =>
        record.key?.startsWith(key),
      );
      const isAccountAdminRestricted =
        isAccountAdminOnlyKey && !isSuperOrTenantAdmin();

      return [
        {
          label: (
            <Tooltip
              title={
                !hasPermission(permissionMap)
                  ? "No access"
                  : record.disable
                    ? disableAccessText
                    : isAccountAdminRestricted
                      ? "Only Account Admins can add/edit/remove armorcode.group tags"
                      : ""
              }
              placement="top"
            >
              <Typography.Text style={{ color: "#70737B" }}>
                Edit
              </Typography.Text>
            </Tooltip>
          ),
          value: "edit",
          icon: <EditIcon />,
          disabled:
            !hasPermission(permissionMap) ||
            record.disable ||
            isAccountAdminRestricted,
        },
        {
          label: (
            <Tooltip
              title={
                !hasPermission(permissionMap)
                  ? "No access"
                  : record.disable
                    ? disableAccessText
                    : isAccountAdminRestricted
                      ? "Only Account Admins can add/edit/remove armorcode.group tags"
                      : ""
              }
            >
              <Typography.Text style={{ color: "#D92C20" }}>
                Delete
              </Typography.Text>{" "}
              {/* color is different in figma thats why giving style and not type*/}
            </Tooltip>
          ),
          value: "delete",
          icon: <TrashIcon style={{ color: "#D92C20" }} />,
          disabled:
            !hasPermission(permissionMap) ||
            record.disable ||
            isAccountAdminRestricted,
        },
      ];
    },
    [permissionMap, disableAccessText, accountAdminOnlyKeys],
  );

  const onTagsActionClick = useCallback(
    (value: string | number, rowIndex: number, edit = false, currentRecord) => {
      if (isFunction(setEditingTags)) {
        setEditingTags(true);
      }
      switch (value) {
        case "edit":
          toggleEdit(rowIndex, edit);
          break;
        case "delete":
          onDelete(rowIndex);
      }
    },
    [onDelete, toggleEdit, setEditingTags],
  );

  const actionRenderer = useCallback(
    (currentRecord: Tag) => {
      // This is needed in case of sorting, The row gets sort but the row index always starts from 0
      // This calculate the record index from the array it resides
      const rowIndex = findIndex(
        dataSource as Tag[],
        (record: Tag) => record.id === currentRecord.id,
      );
      if (currentRecord.isEdit) {
        return (
          <>
            <Tooltip title="Save Tag">
              <ACButton
                onClick={() => onSave(rowIndex)}
                iconOnly
                type="outline"
                size="sm"
                style={{ marginRight: 8 }}
              >
                <SaveIcon />
              </ACButton>
            </Tooltip>
            <Tooltip title="Cancel Edit">
              <ACButton
                onClick={() => {
                  if (isFunction(setEditingTags)) {
                    setEditingTags(false);
                  }
                  toggleEdit(rowIndex, false);
                }}
                iconOnly
                size="sm"
                type="danger"
              >
                <TrashIcon />
              </ACButton>
            </Tooltip>
          </>
        );
      }
      return (
        <ACDropdown
          items={tagsActionItems(currentRecord)}
          menuWidth={120}
          placement="bottomRight"
          onOptionClick={(value: string | number) =>
            onTagsActionClick(value, rowIndex, true, currentRecord)
          }
        >
          <ACButton type="ghost" size="sm" iconOnly>
            <EllipsisVerticalIcon />
          </ACButton>
        </ACDropdown>
      );
    },
    [
      dataSource,
      onSave,
      onTagsActionClick,
      tagsActionItems,
      toggleEdit,
      setEditingTags,
    ],
  );

  const columns = useMemo<ACTableColumnType<Tag>[]>(
    () => [
      {
        title: "Tag",
        dataIndex: "key",
        sorter: Sorters.STRING("key"),
        sortType: "string",
        key: "key",
        width: 180,
        onCell: () => ({ className: "d-flex align-items-center" }),
        render: (value: string, currentRecord: Tag) => {
          // This is needed in case of sorting, The row gets sort but the row index always starts from 0
          // This calculate the record index from the array it resides
          const tagKeys = tagValue.map((item) => item.key);
          const mergedArray =
            mentionOptions !== undefined
              ? [...mentionOptions, ...tagKeys]
              : [...tagKeys];

          if (currentRecord.isEdit) {
            const rowIndex = findIndex(
              dataSource as Tag[],
              (record: Tag) => record.id === currentRecord.id,
            );
            return (
              <AutoComplete
                size={props.size}
                // @ts-expect-error
                onKeyPress={preventEnter}
                style={{ width, maxWidth: "100%" }}
                defaultValue={value}
                name={`key_${rowIndex}`}
                onChange={(value) => onKeyChange(value, rowIndex)}
                filterOption={(inputValue, option) =>
                  ((option as DefaultOptionType).value as string)
                    .toUpperCase()
                    .indexOf(inputValue.toUpperCase()) !== -1
                }
              >
                {map(mergedArray, (option, index) => {
                  return (
                    <AutoComplete.Option
                      key={`${option}_${index}`}
                      value={option}
                    >
                      {option}
                    </AutoComplete.Option>
                  );
                })}
              </AutoComplete>
            );
          }
          return <ACTag label={value} className="ac-tag-table-key" />;
        },
      },
      {
        title: "Value",
        dataIndex: "value",
        key: "value",
        sorter: true,
        width: 200,
        render: (value: string, currentRecord: Tag) => {
          // This is needed in case of sorting, The row gets sort but the row index always starts from 0
          // This calculate the record index from the array it resides

          // Use currentRecord.key directly - it's updated in real-time by onKeyChange
          const tagValues = flatten(
            tagValue
              .filter((record) => record.key === currentRecord.key)
              .map((record) => record.value),
          );
          const mergedArray =
            mentionOptions !== undefined
              ? [...tagValues, ...mentionOptions]
              : [...tagValues];
          if (currentRecord.isEdit) {
            const rowIndex = findIndex(
              dataSource as Tag[],
              (record: Tag) => record.id === currentRecord.id,
            );
            return (
              <AutoComplete
                size={props.size}
                //@ts-expect-error
                name={`value_${rowIndex}`}
                defaultValue={value}
                onKeyPress={preventEnter}
                onChange={(value) => onKeyInputChange(rowIndex, value)}
                style={{ width, maxWidth: "100%" }}
              >
                {map(mergedArray, (option, index) => {
                  return (
                    <AutoComplete.Option
                      key={`${option}_${index}`}
                      value={option}
                    >
                      {option}
                    </AutoComplete.Option>
                  );
                })}
              </AutoComplete>
            );
          }
          return isEmpty(value) ? (
            <span className="ac-tag-table-value-input">-</span>
          ) : (
            value
          );
        },
      },
    ],
    [
      dataSource,
      onKeyInputChange,
      mentionOptions,
      onKeyChange,
      props.size,
      tagValue,
      width,
    ],
  );

  return useMemo(
    () => ({
      dataSource,
      selectedTag,
      tagValue,
      currentRowRef,
      wrapperRef,
      toggleEdit,
      onDelete,
      onKeyInputChange,
      onAdd,
      onKeyChange,
      onSave,
      columns,
      actionRenderer,
    }),
    [
      dataSource,
      selectedTag,
      tagValue,
      currentRowRef,
      wrapperRef,
      onAdd,
      toggleEdit,
      onDelete,
      onKeyInputChange,
      onKeyChange,
      onSave,
      columns,
      actionRenderer,
    ],
  );
}
